import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  SelectChangeEvent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
} from '@mui/material';
import {
  Medication,
  Info,
  Close,
  TipsAndUpdates,
  Calculate,
  MedicalServices,
  Build,
  ArrowBack,
  Add,
  ArrowForwardIos,
  ExpandMore,
} from '@mui/icons-material';
import { RouterLink as Link } from '../common/RouterLink';
import { useCases } from '../../contexts/api-contexts/CasesContext';
import { useTreatments } from '../../contexts/api-contexts/TreatmentsContext';
import { useDiseases } from '../../contexts/api-contexts/DiseasesContext';
import {
  Treatment,
  TreatmentType,
  getTreatmentDisplayName,
  getTreatmentTypeLabel,
  SelectedTreatment,
  CalculatedDosageData,
} from '../../types/treatment.types';
import CaseTreatmentInfoDialog from './CaseTreatmentInfoDialog';
import CaseDosageCalculatorDialog from './CaseDosageCalculatorDialog';
import DrugTreatmentConfirmDialog from './DrugTreatmentConfirmDialog';

interface CaseTreatmentsProps {
  id?: string;
  onNavigate?: (path: string) => void;
  onTabChange?: (
    tab: 'basic' | 'symptoms' | 'diagnoses' | 'treatments' | 'summary'
  ) => void;
}

// Note: selected_treatments is now part of CaseData interface in CasesContext

export const CaseTreatments: React.FC<CaseTreatmentsProps> = ({
  id: _id,
  onNavigate: _onNavigate,
  onTabChange,
}) => {
  const { currentCase, setCaseProperty, persistCase } = useCases();
  const {
    fetchTreatmentSets,
    fetchTreatments,
    getTreatmentSets,
    getTreatments,
    isLoadingTreatmentSets,
    isLoadingTreatments,
    error: treatmentsError,
  } = useTreatments();
  const {
    fetchDisease,
    getDisease,
    isLoading: isLoadingDisease,
  } = useDiseases();

  // State for currently selected diagnosis in dropdown
  const [selectedDiagnosisId, setSelectedDiagnosisId] = useState<number | null>(
    null
  );

  // State for selected protocol for the current diagnosis
  const [selectedProtocolId, setSelectedProtocolId] = useState<number | null>(
    null
  );

  // State for treatment info dialog
  const [treatmentInfoOpen, setTreatmentInfoOpen] = useState(false);
  const [selectedTreatmentForInfo, setSelectedTreatmentForInfo] = useState<{
    treatment: Treatment;
    diseaseName: string;
    protocolName: string;
  } | null>(null);

  // State for dosage calculator dialog
  const [dosageCalcOpen, setDosageCalcOpen] = useState(false);
  const [dosageCalcContext, setDosageCalcContext] = useState<{
    treatment: Treatment;
    diseaseName: string;
    animalTypeName?: string;
    // Whether this is opened in "save mode" (adding treatment with calculation)
    saveMode?: boolean;
    // Whether the treatment is already selected (for updating vs adding)
    isAlreadySelected?: boolean;
  } | null>(null);

  // State for drug treatment confirmation dialog
  const [drugConfirmOpen, setDrugConfirmOpen] = useState(false);
  const [drugConfirmTreatment, setDrugConfirmTreatment] =
    useState<Treatment | null>(null);

  // Get selected diagnoses from case
  const selectedDiagnoses = useMemo(() => {
    return currentCase?.selected_diagnoses || [];
  }, [currentCase?.selected_diagnoses]);

  // Get selected treatments from case
  const selectedTreatments = useMemo(() => {
    return currentCase?.selected_treatments || [];
  }, [currentCase?.selected_treatments]);

  // Count selected treatments per diagnosis
  const treatmentCountByDiagnosis = useMemo(() => {
    const counts = new Map<number, number>();
    selectedTreatments.forEach((t) => {
      counts.set(t.disease_id, (counts.get(t.disease_id) || 0) + 1);
    });
    return counts;
  }, [selectedTreatments]);

  // Auto-select first diagnosis when diagnoses change
  useEffect(() => {
    if (selectedDiagnoses.length > 0 && selectedDiagnosisId === null) {
      setSelectedDiagnosisId(selectedDiagnoses[0].id);
    } else if (
      selectedDiagnoses.length === 0 ||
      !selectedDiagnoses.find((d) => d.id === selectedDiagnosisId)
    ) {
      // Reset if selected diagnosis is no longer valid
      setSelectedDiagnosisId(
        selectedDiagnoses.length > 0 ? selectedDiagnoses[0].id : null
      );
    }
  }, [selectedDiagnoses, selectedDiagnosisId]);

  // Fetch treatment sets and disease info when diagnosis changes
  useEffect(() => {
    if (selectedDiagnosisId !== null) {
      fetchTreatmentSets(selectedDiagnosisId);
      fetchDisease(selectedDiagnosisId);
      // Reset protocol when diagnosis changes
      setSelectedProtocolId(null);
    }
  }, [selectedDiagnosisId, fetchTreatmentSets, fetchDisease]);

  // Get treatment sets for current diagnosis
  const treatmentSets = useMemo(() => {
    if (selectedDiagnosisId === null) return [];
    return getTreatmentSets(selectedDiagnosisId) || [];
  }, [selectedDiagnosisId, getTreatmentSets]);

  // Auto-select protocol only when there's exactly one option
  useEffect(() => {
    if (treatmentSets.length === 1 && selectedProtocolId === null) {
      setSelectedProtocolId(treatmentSets[0].id);
    }
  }, [treatmentSets, selectedProtocolId]);

  // Fetch treatments when protocol changes
  useEffect(() => {
    if (selectedDiagnosisId !== null && selectedProtocolId !== null) {
      fetchTreatments(selectedDiagnosisId, selectedProtocolId);
    }
  }, [selectedDiagnosisId, selectedProtocolId, fetchTreatments]);

  // Get treatments for current diagnosis and protocol
  const availableTreatments = useMemo(() => {
    if (selectedDiagnosisId === null || selectedProtocolId === null) return [];
    return getTreatments(selectedDiagnosisId, selectedProtocolId) || [];
  }, [selectedDiagnosisId, selectedProtocolId, getTreatments]);

  // Include all treatments with selection indicator
  const displayAvailableTreatments = useMemo(() => {
    if (selectedDiagnosisId === null || selectedProtocolId === null) return [];

    return availableTreatments.map((treatment) => ({
      ...treatment,
      isSelected: selectedTreatments.some(
        (st) =>
          st.treatment_id === treatment.id &&
          st.disease_id === selectedDiagnosisId &&
          st.treatment_set_id === selectedProtocolId
      ),
    }));
  }, [
    availableTreatments,
    selectedTreatments,
    selectedDiagnosisId,
    selectedProtocolId,
  ]);

  // Get current diagnosis object
  const currentDiagnosis = useMemo(() => {
    return selectedDiagnoses.find((d) => d.id === selectedDiagnosisId) || null;
  }, [selectedDiagnoses, selectedDiagnosisId]);

  // Get current protocol object
  const currentProtocol = useMemo(() => {
    return treatmentSets.find((s) => s.id === selectedProtocolId) || null;
  }, [treatmentSets, selectedProtocolId]);

  // Get disease info for current diagnosis (includes general treatment info)
  const currentDiseaseInfo = useMemo(() => {
    if (selectedDiagnosisId === null) return null;
    return getDisease(selectedDiagnosisId);
  }, [selectedDiagnosisId, getDisease]);

  // Loading states
  const isLoadingSets = selectedDiagnosisId
    ? isLoadingTreatmentSets(selectedDiagnosisId)
    : false;
  const isLoadingDiseaseInfo = selectedDiagnosisId
    ? isLoadingDisease(selectedDiagnosisId)
    : false;
  const isLoadingTreatmentList =
    selectedDiagnosisId && selectedProtocolId
      ? isLoadingTreatments(selectedDiagnosisId, selectedProtocolId)
      : false;

  // Handle diagnosis dropdown change
  const handleDiagnosisChange = useCallback(
    (event: SelectChangeEvent<number>) => {
      const newDiagnosisId = event.target.value as number;
      setSelectedDiagnosisId(newDiagnosisId);
      // Reset protocol when switching diagnoses
      setSelectedProtocolId(null);
    },
    []
  );

  // Handle protocol selection
  const handleProtocolSelect = useCallback((protocolId: number) => {
    setSelectedProtocolId(protocolId);
  }, []);

  // Add treatment to selected list (internal function without drug confirmation)
  const addTreatmentToCase = useCallback(
    async (treatment: Treatment, calculatedDosage?: CalculatedDosageData) => {
      if (selectedDiagnosisId === null || selectedProtocolId === null) return;

      const newTreatment: SelectedTreatment = {
        treatment_id: treatment.id,
        disease_id: selectedDiagnosisId,
        treatment_set_id: selectedProtocolId,
        type: treatment.type,
        name: getTreatmentDisplayName(treatment),
        calculated_dosage: calculatedDosage,
      };

      const updatedTreatments = [...selectedTreatments, newTreatment];
      setCaseProperty('selected_treatments', updatedTreatments);

      // Persist the case after updating treatments
      try {
        await persistCase();
      } catch (error) {
        console.error('Error persisting case after adding treatment:', error);
      }
    },
    [
      selectedDiagnosisId,
      selectedProtocolId,
      selectedTreatments,
      setCaseProperty,
      persistCase,
    ]
  );

  // Add treatment to selected list - shows confirmation dialog for drugs with doses
  const handleAddTreatment = useCallback(
    (treatment: Treatment) => {
      if (selectedDiagnosisId === null || selectedProtocolId === null) return;

      // For drug treatments with doses available, show confirmation dialog
      if (
        treatment.type === TreatmentType.Drug &&
        treatment.drug_substance?.has_doses !== false
      ) {
        setDrugConfirmTreatment(treatment);
        setDrugConfirmOpen(true);
      } else {
        // For non-drug treatments or drugs without doses, add directly
        addTreatmentToCase(treatment);
      }
    },
    [selectedDiagnosisId, selectedProtocolId, addTreatmentToCase]
  );

  // Handle drug confirmation: add without calculation
  const handleDrugAddWithoutCalculation = useCallback(() => {
    if (drugConfirmTreatment) {
      addTreatmentToCase(drugConfirmTreatment);
    }
    setDrugConfirmOpen(false);
    setDrugConfirmTreatment(null);
  }, [drugConfirmTreatment, addTreatmentToCase]);

  // Handle drug confirmation: open calculator
  const handleDrugCalculateDosage = useCallback(() => {
    if (drugConfirmTreatment) {
      const diseaseName = currentDiagnosis?.name || 'Unknown';
      setDosageCalcContext({
        treatment: drugConfirmTreatment,
        diseaseName,
        saveMode: true, // Enable save mode
      });
      setDosageCalcOpen(true);
    }
    setDrugConfirmOpen(false);
  }, [drugConfirmTreatment, currentDiagnosis]);

  // Handle saving dosage from calculator dialog
  const handleSaveDosage = useCallback(
    (dosageData: CalculatedDosageData) => {
      if (dosageCalcContext?.treatment) {
        addTreatmentToCase(dosageCalcContext.treatment, dosageData);
      }
      setDosageCalcContext(null);
      setDrugConfirmTreatment(null);
    },
    [dosageCalcContext, addTreatmentToCase]
  );

  // Remove treatment from selected list
  const handleRemoveTreatment = useCallback(
    async (treatment: SelectedTreatment) => {
      const updatedTreatments = selectedTreatments.filter(
        (st) =>
          !(
            st.treatment_id === treatment.treatment_id &&
            st.disease_id === treatment.disease_id &&
            st.treatment_set_id === treatment.treatment_set_id
          )
      );
      setCaseProperty('selected_treatments', updatedTreatments);

      // Persist the case after updating treatments
      try {
        await persistCase();
      } catch (error) {
        console.error('Error persisting case after removing treatment:', error);
      }
    },
    [selectedTreatments, setCaseProperty, persistCase]
  );

  // Open treatment info dialog
  const handleOpenTreatmentInfo = useCallback(
    (treatment: Treatment) => {
      const diseaseName = currentDiagnosis?.name || 'Unknown';
      const protocolName = currentProtocol?.name || '';
      setSelectedTreatmentForInfo({ treatment, diseaseName, protocolName });
      setTreatmentInfoOpen(true);
    },
    [currentDiagnosis, currentProtocol]
  );

  // Open dosage calculator dialog
  const handleOpenDosageCalculator = useCallback(
    (treatment: Treatment) => {
      const diseaseName = currentDiagnosis?.name || 'Unknown';

      // Check if this treatment is already selected (for updating dosage)
      const isAlreadySelected =
        selectedDiagnosisId !== null &&
        selectedProtocolId !== null &&
        selectedTreatments.some(
          (st) =>
            st.treatment_id === treatment.id &&
            st.disease_id === selectedDiagnosisId &&
            st.treatment_set_id === selectedProtocolId
        );

      setDosageCalcContext({
        treatment,
        diseaseName,
        // Always enable save mode when opening from treatment list
        // - If already selected: will update the dosage
        // - If not selected: will add with dosage
        saveMode: true,
        isAlreadySelected,
      });
      setDosageCalcOpen(true);
    },
    [
      currentDiagnosis,
      selectedDiagnosisId,
      selectedProtocolId,
      selectedTreatments,
    ]
  );

  // Handle updating dosage for an already selected treatment
  const handleUpdateDosage = useCallback(
    async (dosageData: CalculatedDosageData) => {
      if (
        !dosageCalcContext?.treatment ||
        selectedDiagnosisId === null ||
        selectedProtocolId === null
      )
        return;

      const treatmentId = dosageCalcContext.treatment.id;

      // Find and update the existing treatment
      const updatedTreatments = selectedTreatments.map((st) => {
        if (
          st.treatment_id === treatmentId &&
          st.disease_id === selectedDiagnosisId &&
          st.treatment_set_id === selectedProtocolId
        ) {
          return {
            ...st,
            calculated_dosage: dosageData,
          };
        }
        return st;
      });

      setCaseProperty('selected_treatments', updatedTreatments);

      try {
        await persistCase();
      } catch (error) {
        console.error('Error persisting case after updating dosage:', error);
      }

      setDosageCalcContext(null);
    },
    [
      dosageCalcContext,
      selectedDiagnosisId,
      selectedProtocolId,
      selectedTreatments,
      setCaseProperty,
      persistCase,
    ]
  );

  // Get treatment type icon
  const getTreatmentTypeIcon = (type: TreatmentType) => {
    switch (type) {
      case TreatmentType.Drug:
        return <Medication fontSize="small" />;
      case TreatmentType.Procedure:
        return <MedicalServices fontSize="small" />;
      case TreatmentType.OtherTreatment:
        return <Build fontSize="small" />;
      default:
        return <Medication fontSize="small" />;
    }
  };

  // Get diagnosis name by ID
  const getDiagnosisName = useCallback(
    (diagnosisId: number): string => {
      const diagnosis = selectedDiagnoses.find((d) => d.id === diagnosisId);
      return diagnosis?.name || `Disease ${diagnosisId}`;
    },
    [selectedDiagnoses]
  );

  // Navigate back to diagnoses
  const handleBackToDiagnoses = useCallback(() => {
    onTabChange?.('diagnoses');
  }, [onTabChange]);

  // Group selected treatments by diagnosis for display
  const selectedTreatmentsByDiagnosis = useMemo(() => {
    const grouped = new Map<number, SelectedTreatment[]>();
    selectedTreatments.forEach((t) => {
      const existing = grouped.get(t.disease_id) || [];
      grouped.set(t.disease_id, [...existing, t]);
    });
    return grouped;
  }, [selectedTreatments]);

  return (
    <Card
      elevation={1}
      sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}
    >
      <CardContent sx={{ flexGrow: 1, p: 1, overflow: 'auto' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Medication sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h5" component="h2" sx={{ flexGrow: 1 }}>
            Case Treatments
          </Typography>
          <Tooltip title="Back to diagnoses">
            <IconButton onClick={handleBackToDiagnoses} size="small">
              <ArrowBack />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Error display */}
        {treatmentsError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {treatmentsError}
          </Alert>
        )}

        {/* Selected Treatments Section */}
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="h6"
            sx={{ mb: 1, fontWeight: 600, color: 'text.primary' }}
          >
            Selected Treatments ({selectedTreatments.length})
          </Typography>
          {selectedTreatments.length > 0 ? (
            <List
              dense
              sx={{
                bgcolor: 'background.paper',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              {/* Group by diagnosis */}
              {Array.from(selectedTreatmentsByDiagnosis.entries()).map(
                ([diseaseId, treatments]) => (
                  <React.Fragment key={diseaseId}>
                    {/* Diagnosis header */}
                    <ListItem
                      sx={{
                        bgcolor: 'action.hover',
                        py: 0.5,
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      <ListItemText
                        primary={getDiagnosisName(diseaseId)}
                        primaryTypographyProps={{
                          variant: 'subtitle2',
                          color: 'text.secondary',
                          fontWeight: 600,
                        }}
                      />
                    </ListItem>
                    {/* Treatments for this diagnosis */}
                    {treatments.map((treatment, idx) => (
                      <ListItem
                        key={`${treatment.treatment_id}-${treatment.disease_id}-${idx}`}
                        sx={{ py: 0.5, pl: 3 }}
                      >
                        <Box
                          sx={{ display: 'flex', alignItems: 'center', mr: 1 }}
                        >
                          {getTreatmentTypeIcon(treatment.type)}
                        </Box>
                        <ListItemText
                          primary={
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                              }}
                            >
                              {treatment.name}
                              {treatment.calculated_dosage && (
                                <Chip
                                  icon={<Calculate />}
                                  label={`${treatment.calculated_dosage.result}`}
                                  size="small"
                                  color="success"
                                  variant="outlined"
                                  sx={{ height: 20, fontSize: '0.7rem' }}
                                />
                              )}
                            </Box>
                          }
                          secondary={
                            treatment.calculated_dosage
                              ? `${
                                  treatment.calculated_dosage.dose_type_name ||
                                  ''
                                } ${
                                  treatment.calculated_dosage
                                    .administration_route_name
                                    ? `- ${treatment.calculated_dosage.administration_route_name}`
                                    : ''
                                }`
                              : undefined
                          }
                          primaryTypographyProps={{
                            variant: 'body1',
                            component: 'div',
                          }}
                        />
                        <ListItemSecondaryAction>
                          <Tooltip title="Remove treatment">
                            <IconButton
                              edge="end"
                              size="small"
                              onClick={() => handleRemoveTreatment(treatment)}
                            >
                              <Close fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </React.Fragment>
                )
              )}
            </List>
          ) : (
            <Box
              sx={{
                py: 3,
                px: 2,
                textAlign: 'center',
                bgcolor: 'background.paper',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography variant="body2" color="text.secondary">
                No treatments selected yet. Browse available treatments below
                and add them to your selection.
              </Typography>
            </Box>
          )}

          {/* Navigation link to Summary - show when treatments are selected */}
          {selectedTreatments.length > 0 && (
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Tooltip title="Continue to case summary">
                <span>
                  <Link
                    to={`/cases/${currentCase?.id}?tab=summary`}
                    style={{
                      textDecoration: 'none',
                    }}
                    onClick={(e) => {
                      e.preventDefault();
                      // TODO: Navigate to summary when implemented
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        color: 'primary.main',
                        fontSize: '1.1rem',
                        fontWeight: 'medium',
                        '&:hover': {
                          color: 'primary.dark',
                        },
                      }}
                    >
                      Continue to Summary
                      <ArrowForwardIos sx={{ fontSize: '1rem' }} />
                    </Box>
                  </Link>
                </span>
              </Tooltip>
            </Box>
          )}
        </Box>

        {/* Available Treatments Section */}
        {selectedDiagnoses.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <TipsAndUpdates
                sx={{ mr: 1, color: 'warning.main', fontSize: 18 }}
              />
              <Typography
                variant="h6"
                sx={{ fontWeight: 600, color: 'text.primary' }}
              >
                Available Treatments
              </Typography>
            </Box>

            {/* Diagnosis Selector */}
            <Box sx={{ mb: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel id="diagnosis-select-label">
                  Showing treatments for
                </InputLabel>
                <Select
                  labelId="diagnosis-select-label"
                  id="diagnosis-select"
                  value={selectedDiagnosisId || ''}
                  label="Showing treatments for"
                  onChange={handleDiagnosisChange}
                >
                  {selectedDiagnoses.map((diagnosis) => {
                    const count =
                      treatmentCountByDiagnosis.get(diagnosis.id) || 0;
                    return (
                      <MenuItem key={diagnosis.id} value={diagnosis.id}>
                        {diagnosis.name}
                        {count > 0 && ` (${count} selected)`}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Box>

            {/* General Treatment Information for the Disease */}
            {isLoadingDiseaseInfo && (
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}
              >
                <CircularProgress size={16} />
                <Typography variant="body2" color="text.secondary">
                  Loading disease information...
                </Typography>
              </Box>
            )}

            {!isLoadingDiseaseInfo && currentDiseaseInfo?.treatment_info && (
              <Accordion
                defaultExpanded={false}
                sx={{
                  mb: 2,
                  '&:before': { display: 'none' },
                  boxShadow: 'none',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMore />}
                  sx={{
                    minHeight: 48,
                    '& .MuiAccordionSummary-content': {
                      my: 1,
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Info fontSize="small" color="info" />
                    <Typography variant="subtitle2" color="text.secondary">
                      General Treatment Information
                    </Typography>
                  </Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      mt: 0.5,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      opacity: 0.8,
                      fontSize: '0.8rem',
                    }}
                  >
                    {currentDiseaseInfo.treatment_info}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                  <Typography
                    variant="body2"
                    sx={{ whiteSpace: 'pre-line', color: 'text.secondary' }}
                  >
                    {currentDiseaseInfo.treatment_info}
                  </Typography>
                  {currentDiseaseInfo.follow_up && (
                    <Box sx={{ mt: 2 }}>
                      <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        sx={{ mb: 0.5 }}
                      >
                        Follow Up
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          whiteSpace: 'pre-line',
                          color: 'text.secondary',
                        }}
                      >
                        {currentDiseaseInfo.follow_up}
                      </Typography>
                    </Box>
                  )}
                </AccordionDetails>
              </Accordion>
            )}

            {/* Protocol Selector */}
            {isLoadingSets && (
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}
              >
                <CircularProgress size={20} />
                <Typography variant="body2" color="text.secondary">
                  Loading treatment protocols...
                </Typography>
              </Box>
            )}

            {!isLoadingSets && treatmentSets.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  sx={{ mb: 1 }}
                >
                  Protocol
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {treatmentSets.map((set) => (
                    <Chip
                      key={set.id}
                      label={set.name}
                      color={
                        selectedProtocolId === set.id ? 'primary' : 'default'
                      }
                      variant={
                        selectedProtocolId === set.id ? 'filled' : 'outlined'
                      }
                      onClick={() => handleProtocolSelect(set.id)}
                      sx={{ cursor: 'pointer' }}
                    />
                  ))}
                </Box>
                {selectedProtocolId === null && treatmentSets.length > 1 && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 1.5, fontStyle: 'italic' }}
                  >
                    Select a treatment protocol to view available treatments.
                    Review the general treatment information above to help
                    choose the appropriate protocol.
                  </Typography>
                )}
              </Box>
            )}

            {!isLoadingSets && treatmentSets.length === 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                No treatment protocols available for this diagnosis.
              </Alert>
            )}

            {/* Loading treatments */}
            {isLoadingTreatmentList && (
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}
              >
                <CircularProgress size={20} />
                <Typography variant="body2" color="text.secondary">
                  Loading treatments...
                </Typography>
              </Box>
            )}

            {/* Treatments List */}
            {!isLoadingTreatmentList &&
              displayAvailableTreatments.length > 0 && (
                <List
                  dense
                  sx={{
                    bgcolor: 'background.paper',
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  {displayAvailableTreatments.map((treatment) => (
                    <ListItem
                      key={treatment.id}
                      sx={{
                        py: 0.5,
                        cursor: 'pointer',
                        bgcolor: treatment.isSelected
                          ? 'action.selected'
                          : 'inherit',
                        opacity: treatment.isSelected ? 0.7 : 1,
                        '&:hover': {
                          backgroundColor: treatment.isSelected
                            ? 'action.selected'
                            : 'action.hover',
                        },
                      }}
                      onClick={() => handleOpenTreatmentInfo(treatment)}
                    >
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', mr: 1 }}
                      >
                        {getTreatmentTypeIcon(treatment.type)}
                      </Box>
                      <IconButton
                        edge="start"
                        aria-label="show info"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenTreatmentInfo(treatment);
                        }}
                        size="small"
                        sx={{ color: 'info.main', mr: 1 }}
                      >
                        <Info fontSize="small" />
                      </IconButton>
                      <ListItemText
                        primary={
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                            }}
                          >
                            {getTreatmentDisplayName(treatment)}
                            {treatment.isSelected && (
                              <Typography
                                component="span"
                                variant="caption"
                                sx={{
                                  bgcolor: 'success.light',
                                  color: 'success.contrastText',
                                  px: 0.75,
                                  py: 0.25,
                                  borderRadius: 1,
                                  fontSize: '0.7rem',
                                  fontWeight: 600,
                                }}
                              >
                                Selected
                              </Typography>
                            )}
                          </Box>
                        }
                        secondary={
                          treatment.treatment_optionality
                            ? treatment.treatment_optionality.name
                            : getTreatmentTypeLabel(treatment.type)
                        }
                        primaryTypographyProps={{
                          variant: 'body1',
                          component: 'div',
                        }}
                      />
                      <ListItemSecondaryAction
                        sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                      >
                        {/* Dosage Calculator Button - only for drugs with dosages available */}
                        {treatment.type === TreatmentType.Drug &&
                          treatment.drug_substance?.has_doses !== false && (
                            <Tooltip title="Calculate dosage">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenDosageCalculator(treatment);
                                }}
                                color="secondary"
                              >
                                <Calculate fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        {!treatment.isSelected && (
                          <Tooltip title="Add treatment">
                            <IconButton
                              edge="end"
                              aria-label="add treatment"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleAddTreatment(treatment);
                              }}
                              size="small"
                              sx={{ color: 'success.main' }}
                            >
                              <Add fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              )}

            {/* No treatments available in selected protocol */}
            {!isLoadingTreatmentList &&
              selectedProtocolId &&
              displayAvailableTreatments.length === 0 && (
                <Box
                  sx={{
                    py: 2,
                    px: 2,
                    textAlign: 'center',
                    bgcolor: 'background.paper',
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    No treatments available for this protocol.
                  </Typography>
                </Box>
              )}
          </Box>
        )}

        {/* No diagnoses selected */}
        {selectedDiagnoses.length === 0 && (
          <Alert
            severity="warning"
            action={
              <IconButton
                color="inherit"
                size="small"
                onClick={handleBackToDiagnoses}
              >
                <ArrowBack fontSize="small" />
              </IconButton>
            }
          >
            No diagnoses selected. Please select diagnoses first to view
            treatment options.
          </Alert>
        )}

        {/* Continue to Summary button */}
        {selectedTreatments.length > 0 && (
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              color="primary"
              endIcon={<ArrowForwardIos />}
              onClick={() => onTabChange?.('summary')}
            >
              Continue to Summary
            </Button>
          </Box>
        )}
      </CardContent>

      {/* Treatment Info Dialog */}
      <CaseTreatmentInfoDialog
        open={treatmentInfoOpen}
        onClose={() => setTreatmentInfoOpen(false)}
        treatmentInfo={selectedTreatmentForInfo}
        onCalculateDosage={handleOpenDosageCalculator}
        onAddToSelected={
          selectedTreatmentForInfo
            ? () => handleAddTreatment(selectedTreatmentForInfo.treatment)
            : undefined
        }
        isSelected={
          selectedTreatmentForInfo && selectedDiagnosisId && selectedProtocolId
            ? selectedTreatments.some(
                (st) =>
                  st.treatment_id === selectedTreatmentForInfo.treatment.id &&
                  st.disease_id === selectedDiagnosisId &&
                  st.treatment_set_id === selectedProtocolId
              )
            : false
        }
      />

      {/* Drug Treatment Confirmation Dialog */}
      <DrugTreatmentConfirmDialog
        open={drugConfirmOpen}
        onClose={() => {
          setDrugConfirmOpen(false);
          setDrugConfirmTreatment(null);
        }}
        treatment={drugConfirmTreatment}
        onAddWithoutCalculation={handleDrugAddWithoutCalculation}
        onCalculateDosage={handleDrugCalculateDosage}
      />

      {/* Dosage Calculator Dialog */}
      <CaseDosageCalculatorDialog
        open={dosageCalcOpen}
        onClose={() => {
          setDosageCalcOpen(false);
          setDosageCalcContext(null);
          setDrugConfirmTreatment(null);
        }}
        treatment={dosageCalcContext?.treatment || null}
        patient={currentCase?.patient}
        diseaseName={dosageCalcContext?.diseaseName}
        animalTypeName={dosageCalcContext?.animalTypeName}
        showSaveButton={dosageCalcContext?.saveMode}
        onSave={(dosageData) => {
          // Check if treatment is already selected (update) or new (add with dosage)
          if (dosageCalcContext?.isAlreadySelected) {
            handleUpdateDosage(dosageData);
          } else {
            handleSaveDosage(dosageData);
          }
        }}
      />
    </Card>
  );
};

export default CaseTreatments;
